/**
 * Keeping every open app on the current release.
 *
 * The server reports the running release in `/api/status`. The app remembers
 * the version it booted with and watches for that to change. When it does the
 * page reloads itself, which is safe because the app holds nothing that is not
 * already on the server.
 *
 * The only question is when. Reloading while someone is typing an amount, or
 * while a trade is in flight, would lose what they were doing, so a reload
 * waits for a moment where nothing is at stake. Those moments are common: the
 * app is idle, or it has just come back to the foreground.
 */

export interface ReloadConditions {
  /** A request the app started has not finished. */
  readonly busy: boolean;
  /** A panel or sheet is open over the screen. */
  readonly overlayOpen: boolean;
  /** The person has typed something that would be lost. */
  readonly hasTypedText: boolean;
  /** The keyboard is up: they are in the middle of a thought. */
  readonly inputFocused: boolean;
}

/** Is this a moment where reloading costs the person nothing? */
export function safeToReload(c: ReloadConditions): boolean {
  return !c.busy && !c.overlayOpen && !c.hasTypedText && !c.inputFocused;
}

/**
 * Reads the conditions the DOM can answer for itself. An open PIN pad or sheet
 * is an overlay; a screen that has marked the document busy (see `markBusy`)
 * is in the middle of something.
 */
export function domConditions(): ReloadConditions {
  const tag = document.activeElement?.tagName ?? '';
  const inputFocused = tag === 'INPUT' || tag === 'TEXTAREA';
  const hasTypedText = [...document.querySelectorAll('input, textarea')].some(
    (el) => (el as HTMLInputElement).value.trim() !== '',
  );
  const overlayOpen = document.querySelector('.overlay, [data-testid="pinpad"]') !== null;
  const busy = document.documentElement.dataset.busy === '1';
  return { busy, overlayOpen, hasTypedText, inputFocused };
}

/** Screens call this around anything that must not be interrupted by a reload. */
export function markBusy(on: boolean): void {
  if (on) document.documentElement.dataset.busy = '1';
  else delete document.documentElement.dataset.busy;
}

const UPDATED_KEY = 'xchange.updated';
/** Remember that the coming reload is an update, so the next boot can say so. */
export function reloadForUpdate(): void {
  try {
    sessionStorage.setItem(UPDATED_KEY, '1');
  } catch {
    /* fine */
  }
  window.location.reload();
}
/** True once, on the boot right after an update reload. */
export function justUpdated(): boolean {
  try {
    if (sessionStorage.getItem(UPDATED_KEY) === null) return false;
    sessionStorage.removeItem(UPDATED_KEY);
    return true;
  } catch {
    return false;
  }
}
